/**
 * Image Job Card Component - Glass Morphism Design
 * Displays a single image generation job in the history
 * Validates: Requirements 16.5
 */
import { ImageJob } from '../../stores/imageStore';
import { Icons } from '../icons';
import { THEME } from '../../styles/theme';

interface ImageJobCardProps {
  job: ImageJob;
  isSelected: boolean;
  onClick: () => void;
}

export function ImageJobCard({ job, isSelected, onClick }: ImageJobCardProps) {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusColor = (): string => {
    switch (job.status) {
      case 'completed':
        return THEME.success;
      case 'processing':
        return THEME.info;
      case 'failed':
        return THEME.error;
      default:
        return THEME.textMuted;
    }
  };

  const getStatusText = (): string => {
    switch (job.status) {
      case 'completed':
        return 'Completed';
      case 'processing':
        return 'Processing...';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  // Get first image for thumbnail
  const thumbnailUrl = job.images?.[0]?.url;

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl overflow-hidden transition-all duration-200 border"
      style={{
        backgroundColor: isSelected ? THEME.accentSoft : 'rgba(255,255,255,0.03)',
        borderColor: isSelected ? THEME.accentBorder : THEME.border,
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.05)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.03)';
        }
      }}
    >
      {/* Thumbnail */}
      <div
        className="aspect-square relative"
        style={{ backgroundColor: THEME.bgSurface }}
      >
        {thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={job.prompt}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            {job.status === 'processing' ? (
              <Icons.Refresh
                size={32}
                className="animate-spin"
                style={{ color: THEME.accentColor }}
              />
            ) : job.status === 'failed' ? (
              <Icons.X
                size={32}
                style={{ color: THEME.error }}
              />
            ) : (
              <Icons.Image
                size={32}
                style={{ color: THEME.textMuted }}
              />
            )}
          </div>
        )}

        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
              job.status === 'processing' ? 'animate-pulse' : ''
            }`}
            style={{
              backgroundColor: 'rgba(0,0,0,0.7)',
              backdropFilter: 'blur(10px)',
              color: getStatusColor(),
            }}
          >
            {getStatusText()}
          </span>
        </div>

        {/* Image Count Badge */}
        {job.images && job.images.length > 1 && (
          <div className="absolute bottom-2 right-2">
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
              style={{
                backgroundColor: 'rgba(0,0,0,0.7)',
                backdropFilter: 'blur(10px)',
                color: THEME.textMain,
              }}
            >
              {job.images.length} images
            </span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-3">
        <p className="text-sm line-clamp-2 mb-2" style={{ color: THEME.textMain }}>
          {job.enhancedPrompt || job.prompt}
        </p>
        <div className="flex items-center justify-between text-xs" style={{ color: THEME.textMuted }}>
          <span>{job.model}</span>
          <span>{formatDate(job.createdAt)}</span>
        </div>
        {job.settings.size && (
          <div className="mt-1 text-xs" style={{ color: THEME.textMuted }}>
            {job.settings.size}
          </div>
        )}
      </div>
    </button>
  );
}
